'use client';

import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useState } from 'react';

import type { CartLine } from '@/entities/cart';
import { useRemoveCartLine, useUpdateCartLine } from '@/entities/cart/client';
import {
  colorName,
  findVariant,
  isColorAvailable,
  optionAvailability,
  productHref,
  ProductMedia,
  VariantPicker,
  type Product,
  type Selection,
} from '@/entities/product';
import { productCardsQueryOptions } from '@/entities/product/client';
import { formatMoney } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { QuantityStepper } from '@/shared/ui/quantity-stepper';
import { TextButton } from '@/shared/ui/text-link';

import styles from './cart-line-row.module.scss';

export type CartLineRowProps = {
  line: CartLine;
  index: number;
  /** One edit popover at a time (04B): the view owns which row is edited. */
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onRemoved: (line: CartLine, index: number) => void;
  onError: (message: string) => void;
  onChanged: () => void;
};

function useProduct(slug: string, enabled: boolean) {
  return useQuery({ ...productCardsQueryOptions([slug]), enabled });
}

function VariantEditor({
  line,
  product,
  onClose,
  onSaved,
}: {
  line: CartLine;
  product: Product;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selection, setSelection] = useState<Selection>({ colorId: line.colorId, size: line.size });
  const [tip, setTip] = useState('');
  const update = useUpdateCartLine();
  const hasSizes = product.sizes.length > 1;

  const pickColor = (colorId: string) => {
    const name = colorName(product, colorId) ?? '';
    if (!isColorAvailable(product, colorId)) {
      setTip('Этого цвета сейчас нет в наличии');
      return;
    }
    if (
      hasSizes &&
      selection.size &&
      optionAvailability(product, colorId, selection.size) === 'out_of_stock'
    ) {
      setSelection({ colorId, size: null });
      setTip(`Размера ${selection.size} нет в наличии в цвете «${name}» — выберите другой`);
      return;
    }
    setSelection({ ...selection, colorId });
    setTip('');
  };
  const pickSize = (size: string) => {
    if (optionAvailability(product, selection.colorId, size) === 'out_of_stock') {
      setTip(`Размера ${size} сейчас нет в наличии`);
      return;
    }
    setSelection({ ...selection, size });
    setTip('');
  };
  const save = () => {
    if (hasSizes && !selection.size) {
      setTip('Выберите размер');
      return;
    }
    const variant = findVariant(product, selection);
    if (!variant || variant.availability === 'out_of_stock') {
      setTip('Этого варианта сейчас нет в наличии');
      return;
    }
    if (variant.sku === line.sku) {
      onClose();
      return;
    }
    update.mutate(
      { lineId: line.lineId, sku: variant.sku, quantity: null },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
        onError: (error) => setTip(error.message),
      },
    );
  };

  return (
    <dialog open className={styles.editor} aria-label={`Изменить вариант: ${line.name}`}>
      <div className={styles['editor-head']}>
        <span className={styles.eyebrow}>Изменить вариант</span>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Закрыть">
          ×
        </button>
      </div>
      <VariantPicker
        idPrefix={`edit-${line.lineId}`}
        product={product}
        colorId={selection.colorId}
        size={selection.size}
        onColorChange={pickColor}
        onSizeChange={pickSize}
        density="compact"
        tip={tip}
      />
      <div className={styles['editor-actions']}>
        <Button size="sm" loading={update.isPending} onClick={save}>
          Сохранить
        </Button>
        <TextButton tone="muted" onClick={onClose}>
          Отмена
        </TextButton>
      </div>
    </dialog>
  );
}

/** Cart row (design 04B): quantity, remove with undo, and in-place variant change. */
export function CartLineRow({
  line,
  index,
  editing,
  onEditingChange,
  onRemoved,
  onError,
  onChanged,
}: CartLineRowProps) {
  const setEditing = onEditingChange;
  const update = useUpdateCartLine();
  const remove = useRemoveCartLine();
  const product = useProduct(line.productSlug, editing && line.editable);
  const editable = line.editable && line.productKind === 'item';
  const href = productHref({ kind: line.productKind, slug: line.productSlug });

  const setQuantity = (quantity: number) => {
    onChanged();
    update.mutate(
      { lineId: line.lineId, quantity, sku: null },
      { onError: (error) => onError(error.message) },
    );
  };
  const removeLine = () => {
    setEditing(false);
    remove.mutate(
      { lineId: line.lineId },
      {
        onSuccess: () => onRemoved(line, index),
        onError: (error) => onError(error.message),
      },
    );
  };

  return (
    <li className={styles.row} aria-busy={update.isPending || remove.isPending || undefined}>
      <Link href={href} className={styles.photo} aria-label={line.name} tabIndex={-1}>
        <ProductMedia
          media={line.media}
          fallbackLabel={line.name}
          sizes="144px"
          className={styles.media}
          placeholderSub=""
          sketchSize="62%"
          position="center top"
        />
      </Link>
      <div className={styles.info}>
        {line.availability === 'preorder' && <span className={styles.badge}>Предзаказ</span>}
        <Link href={href} className={styles.name}>
          {line.name}
        </Link>
        <span className={styles.variant}>{line.variantLabel}</span>
        <div className={styles.actions}>
          {editable && (
            <TextButton aria-expanded={editing} onClick={() => setEditing(!editing)}>
              Изменить
            </TextButton>
          )}
          <TextButton
            tone="muted"
            aria-label={`Удалить из корзины: ${line.name}`}
            onClick={removeLine}
            disabled={remove.isPending}
          >
            Удалить
          </TextButton>
        </div>
      </div>
      <QuantityStepper
        className={styles.qty}
        value={line.quantity}
        onChange={setQuantity}
        disabled={update.isPending}
        label={`Количество: ${line.name}`}
        itemName={line.name}
      />
      <div className={styles.price}>
        <span className={styles.total}>{formatMoney(line.lineTotal)}</span>
        {line.quantity > 1 && (
          <span className={styles.unit}>
            {formatMoney(line.unitPrice)} × {line.quantity}
          </span>
        )}
      </div>
      {editing &&
        (product.data?.[0] ? (
          <VariantEditor
            line={line}
            product={product.data[0]}
            onClose={() => setEditing(false)}
            onSaved={onChanged}
          />
        ) : (
          <output className={styles.editor}>
            {product.isError ? 'Не удалось загрузить варианты' : 'Загружаем варианты…'}
          </output>
        ))}
    </li>
  );
}
